import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ThreeWayMatchSummary = ({ poId }) => {
  // Data state
  const [po, setPo] = useState(null);
  const [goodsReceipts, setGoodsReceipts] = useState([]);
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch all data in parallel on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError('');
        const token = localStorage.getItem('authToken');
        const headers = { Authorization: `Bearer ${token}` };

        const [poRes, grRes, siRes, cnRes] = await Promise.all([
          axios.get(`/api/purchase-orders/${poId}`, { headers }),
          axios.get(`/api/goods-receipts?poId=${poId}`, { headers }),
          axios.get(`/api/supplier-invoices?poId=${poId}`, { headers }),
          axios.get(`/api/credit-notes?poId=${poId}`, { headers })
        ]);

        setPo(poRes.data.data || poRes.data);
        setGoodsReceipts((grRes.data.data || grRes.data || []).slice(0, 1)); // Take first GR
        setSupplierInvoices((siRes.data.data || siRes.data || []).slice(0, 1)); // Take first SI
        setCreditNotes(cnRes.data.data || cnRes.data || []);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching 3-way match data:', err);
        setError(
          err.response?.data?.message || err.message || 'Failed to load summary'
        );
        setLoading(false);
      }
    };

    if (poId) {
      fetchData();
    }
  }, [poId]);

  // Helper functions
  const getMatchStatusBadge = (matchStatus) => {
    const statusMap = {
      matched: { bg: 'bg-green-100', text: 'text-green-800', label: '✓ Matched' },
      qty_mismatch: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Qty Mismatch' },
      price_mismatch: {
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        label: 'Price Mismatch'
      },
      both_mismatch: { bg: 'bg-red-100', text: 'text-red-800', label: 'Both Mismatch' },
      pending_match: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending Match' }
    };
    return statusMap[matchStatus] || { bg: 'bg-gray-100', text: 'text-gray-800', label: matchStatus };
  };

  const getCNStatusBadge = (status) => {
    const statusMap = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };
    return statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  };

  const gr = goodsReceipts[0];
  const si = supplierInvoices[0];
  const cn = creditNotes[0];

  // Timeline logic
  const timelineSteps = [
    {
      step: 1,
      label: 'PO Created',
      completed: !!po,
      note: po?.poNumber || ''
    },
    {
      step: 2,
      label: 'Goods Received',
      completed: !!gr,
      note: gr ? `${gr.receiptNumber} (${gr.isCompleted ? 'Complete' : 'Partial'})` : ''
    },
    {
      step: 3,
      label: 'Supplier Invoice Submitted',
      completed: !!si,
      note: si?.invoiceNumber || ''
    },
    {
      step: 4,
      label: '3-Way Match Run',
      completed: !!si && si.matchStatus && si.matchStatus !== 'pending',
      note: si?.matchStatus || ''
    },
    {
      step: 5,
      label: 'Credit Note Generated',
      completed: !!cn,
      note: cn?.creditNoteNumber || '',
      skip: si?.matchStatus === 'matched'
    },
    {
      step: 6,
      label: 'Credit Note Approved',
      completed: cn?.status === 'approved',
      note: cn?.status || '',
      skip: si?.matchStatus === 'matched'
    },
    {
      step: 7,
      label: 'Journal Entry (Draft)',
      completed: !!si?.journalEntry,
      note: si?.journalEntry?.entryNumber || ''
    },
    {
      step: 8,
      label: 'Journal Entry Posted',
      completed: si?.journalEntry?.status === 'posted',
      note: si?.glPostingStatus || ''
    }
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-center py-12">
        <div className="text-gray-500">Loading 3-way match summary...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 4-Card Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card 1: Purchase Order */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Purchase Order</h3>
          <div className="space-y-2 text-sm">
            {po ? (
              <>
                <div>
                  <p className="text-gray-600">PO Number</p>
                  <p className="font-mono font-semibold text-gray-900">{po.poNumber}</p>
                </div>
                <div>
                  <p className="text-gray-600">Supplier</p>
                  <p className="font-semibold text-gray-900">
                    {po.supplierName || po.supplier?.name || 'N/A'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-gray-600">Order Date</p>
                    <p className="text-gray-900">
                      {new Date(po.orderDate).toLocaleDateString('en-PH', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Expected Delivery</p>
                    <p className="text-gray-900">
                      {po.expectedDeliveryDate
                        ? new Date(po.expectedDeliveryDate).toLocaleDateString('en-PH', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <p className="text-gray-600">Total Amount</p>
                  <p className="font-semibold text-lg text-gray-900">
                    ₱{Number(po.total || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                    {po.status}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-gray-500">No data</p>
            )}
          </div>
        </div>

        {/* Card 2: Goods Receipt */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Goods Receipt</h3>
          <div className="space-y-2 text-sm">
            {gr ? (
              <>
                <div>
                  <p className="text-gray-600">GR Number</p>
                  <p className="font-mono font-semibold text-gray-900">{gr.receiptNumber}</p>
                </div>
                <div>
                  <p className="text-gray-600">Received Date</p>
                  <p className="text-gray-900">
                    {new Date(gr.createdAt || gr.updatedAt).toLocaleDateString('en-PH', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <p className="text-gray-600">Total Received</p>
                  <p className="font-semibold text-lg text-gray-900">
                    {gr.receiptItems?.length || 0} items
                  </p>
                </div>
                <div>
                  {gr.isCompleted ? (
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                      Fully Received
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-semibold">
                      Partial Delivery
                    </span>
                  )}
                </div>
              </>
            ) : (
              <p className="text-gray-500">No receipt yet</p>
            )}
          </div>
        </div>

        {/* Card 3: Supplier Invoice */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Supplier Invoice</h3>
          <div className="space-y-2 text-sm">
            {si ? (
              <>
                <div>
                  <p className="text-gray-600">SINV Number</p>
                  <p className="font-mono font-semibold text-gray-900">
                    {si.invoiceNumber}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-gray-600">Invoice Date</p>
                    <p className="text-gray-900">
                      {new Date(si.invoiceDate).toLocaleDateString('en-PH', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Due Date</p>
                    <p className="text-gray-900">
                      {new Date(si.dueDate).toLocaleDateString('en-PH', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <p className="text-gray-600">Total Amount</p>
                  <p className="font-semibold text-lg text-gray-900">
                    ₱{Number(si.totalAmount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  {si.matchStatus ? (
                    (() => {
                      const badge = getMatchStatusBadge(si.matchStatus);
                      return (
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}
                        >
                          {badge.label}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-semibold">
                      Pending Match
                    </span>
                  )}
                </div>
              </>
            ) : (
              <p className="text-gray-500">No invoice yet</p>
            )}
          </div>
        </div>

        {/* Card 4: Credit Note */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Credit Note</h3>
          <div className="space-y-2 text-sm">
            {cn ? (
              <>
                <div>
                  <p className="text-gray-600">CN Number</p>
                  <p className="font-mono font-semibold text-gray-900">
                    {cn.creditNoteNumber}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Reason</p>
                  <p className="text-gray-900 capitalize">{cn.reason?.replace(/_/g, ' ')}</p>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <p className="text-gray-600">CN Amount</p>
                  <p className="font-semibold text-lg text-gray-900">
                    ₱{Number(cn.totalAmount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  {(() => {
                    const badge = getCNStatusBadge(cn.status);
                    return (
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}
                      >
                        {badge.label}
                      </span>
                    );
                  })()}
                </div>
              </>
            ) : (
              <p className="text-gray-500">None</p>
            )}
          </div>
        </div>
      </div>

      {/* Journal Entry Row */}
      {si?.journalEntry && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Journal Entry</h3>
              <p className="text-sm text-gray-600">
                Reference:{' '}
                <span className="font-mono font-semibold text-gray-900">
                  {si.journalEntry.entryNumber}
                </span>
              </p>
            </div>
            <div>
              {si.journalEntry.status === 'posted' ? (
                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                  Posted
                </span>
              ) : (
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 rounded text-xs font-semibold">
                  Draft — Pending Finance Review
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-6">Match Timeline</h3>
        <div className="space-y-4">
          {timelineSteps.map((item, index) => {
            if (item.skip) return null;

            const isLast = index === timelineSteps.length - 1;
            const isMismatch = si?.matchStatus && !['matched', 'pending_match'].includes(si.matchStatus);

            return (
              <div key={item.step} className="flex gap-4">
                {/* Timeline marker */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      item.completed
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {item.completed ? '✓' : '○'}
                  </div>
                  {!isLast && (
                    <div
                      className={`w-0.5 h-8 mt-2 ${
                        item.completed ? 'bg-teal-200' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>

                {/* Step content */}
                <div className="pb-4">
                  <p className="text-sm font-semibold text-gray-900">{item.label}</p>
                  {item.note && (
                    <p className="text-xs text-gray-600 mt-1">
                      {item.note}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ThreeWayMatchSummary;
